import { FontIcon, IconButton, Stack } from "@fluentui/react";
import React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useNavigate, useSearchParams } from "react-router";
import { useAppContext } from './AppContext';
import ProgressBar from "./ProgressBar";
import SConsciousApiService from "../services/SConsciousApiService";
import { MdUpload } from "react-icons/md";
import { version } from "os";
import ActionButton from "./ActionButton";
//import appInsights from "../utils/AppInsights";

interface CameraProps {
    /** When provided, the camera works in callback mode: capture calls onCapture(imgSrc) instead of uploading a receipt. */
    onCapture?: (imgSrc: string) => void;
    /** Called when the user wants to dismiss the camera overlay (callback mode only). */
    onClose?: () => void;
}

export default function Camera({ onCapture, onClose }: CameraProps = {}) {

    let _sConsciousApiService = new SConsciousApiService();

    const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
    const navigate = useNavigate();
    const webcamRef = useRef<any>(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [imgSrc, setImgSrc] = useState<string | null>(null);
    const {appData, setAppData} = useAppContext(); // <-- get context method
    const [searchParams] = useSearchParams();
    let page = searchParams.get("p");
    const userId = localStorage.getItem('signed_in_id') || '';
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── Camera optimisation settings ────────────────────────────────
    type CameraOpts = {
        autofocus: boolean;
        focusDistance: boolean;
        autoExposure: boolean;
        autoWhiteBalance: boolean;
        disableZoom: boolean;
        maxBrightness: boolean;
        maxISO: boolean;
        maxSharpness: boolean;
        torch: boolean;
    };
    const defaultOpts: CameraOpts = {
        autofocus: true, focusDistance: true, autoExposure: true,
        autoWhiteBalance: true, disableZoom: true, maxBrightness: true,
        maxISO: true, maxSharpness: true, torch: true,
    };
    const [showSettings, setShowSettings] = useState(false);
    const [cameraOpts, setCameraOpts] = useState<CameraOpts>(() => {
        try { return { ...defaultOpts, ...JSON.parse(localStorage.getItem('cameraOpts') || '{}') }; }
        catch { return defaultOpts; }
    });
    const [pendingOpts, setPendingOpts] = useState<CameraOpts>(cameraOpts);
    const [supportedCaps, setSupportedCaps] = useState<Partial<Record<keyof CameraOpts, boolean>>>({});
    const [applyStatus, setApplyStatus] = useState<'idle' | 'ok' | 'saved' | 'error'>('idle');
    const cameraOptsRef = useRef(cameraOpts);
    useEffect(() => { cameraOptsRef.current = cameraOpts; }, [cameraOpts]);

    const OPT_LABELS: { key: keyof CameraOpts; label: string }[] = [
        { key: 'autofocus',       label: 'Continuous autofocus' },
        { key: 'focusDistance',   label: 'Optimal focus distance' },
        { key: 'autoExposure',    label: 'Auto exposure' },
        { key: 'autoWhiteBalance',label: 'Auto white balance' },
        { key: 'disableZoom',     label: 'Disable zoom' },
        { key: 'maxBrightness',   label: 'Max brightness' },
        { key: 'maxISO',          label: 'Max ISO' },
        { key: 'maxSharpness',    label: 'Max sharpness' },
        { key: 'torch',           label: 'Flash / torch' },
    ];

    // Build and push constraints for a given opts set onto a live stream.
    // Each constraint is applied individually so a single driver rejection doesn't block the rest.
    const applyConstraints = (opts: CameraOpts, stream: MediaStream, onDone?: (status: 'ok' | 'saved' | 'error') => void) => {
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack) { onDone?.('error'); return; }
        const capabilities = videoTrack.getCapabilities() as any;
        const capsEmpty = !capabilities || Object.keys(capabilities).length === 0;

        if (capsEmpty) {
           // console.log('Camera does not expose capabilities — settings saved, applied on next stream.');
            onDone?.('saved'); return;
        }

        // Detect which opts the camera actually supports
        const supported: Partial<Record<keyof CameraOpts, boolean>> = {
            autofocus:        !!(capabilities.focusMode),
            focusDistance:    !!(capabilities.focusDistance),
            autoExposure:     !!(capabilities.exposureMode),
            autoWhiteBalance: !!(capabilities.whiteBalanceMode),
            disableZoom:      !!(capabilities.zoom),
            maxBrightness:    !!(capabilities.brightness),
            maxISO:           !!(capabilities.iso),
            maxSharpness:     !!(capabilities.sharpness),
            torch:            !!(capabilities.torch),
        };
        setSupportedCaps(supported);

        // Build the full desired constraint map
        const desired: Record<string, any> = {};

        if (capabilities.focusMode) {
            if (opts.autofocus && capabilities.focusMode.includes('continuous'))
                desired.focusMode = 'continuous';
            else if (!opts.autofocus && capabilities.focusMode.includes('manual'))
                desired.focusMode = 'manual';
        }
        if (capabilities.focusDistance && !opts.autofocus)
            desired.focusDistance = opts.focusDistance
                ? { ideal: capabilities.focusDistance.min ?? 0 }
                : { ideal: (capabilities.focusDistance.min + capabilities.focusDistance.max) / 2 };
        if (capabilities.exposureMode) {
            if (opts.autoExposure && capabilities.exposureMode.includes('continuous'))
                desired.exposureMode = 'continuous';
            else if (!opts.autoExposure && capabilities.exposureMode.includes('manual'))
                desired.exposureMode = 'manual';
        }
        if (capabilities.whiteBalanceMode) {
            if (opts.autoWhiteBalance && capabilities.whiteBalanceMode.includes('continuous'))
                desired.whiteBalanceMode = 'continuous';
            else if (!opts.autoWhiteBalance && capabilities.whiteBalanceMode.includes('manual'))
                desired.whiteBalanceMode = 'manual';
        }
        if (capabilities.zoom)
            desired.zoom = capabilities.zoom.min ?? 1.0;
        if (capabilities.brightness)
            desired.brightness = opts.maxBrightness
                ? capabilities.brightness.max
                : Math.round((capabilities.brightness.min + capabilities.brightness.max) / 2);
        if (capabilities.iso)
            desired.iso = opts.maxISO
                ? { ideal: capabilities.iso.max }
                : { ideal: Math.round((capabilities.iso.min + capabilities.iso.max) / 2) };
        if (capabilities.sharpness)
            desired.sharpness = opts.maxSharpness
                ? capabilities.sharpness.max
                : Math.round((capabilities.sharpness.min + capabilities.sharpness.max) / 2);
        if (capabilities.torch)
            desired.torch = opts.torch;

        // Apply each constraint individually — one driver rejection won't block the rest
        const entries = Object.entries(desired);
        //('Applying constraints individually:', JSON.stringify(desired));

        const applyNext = async () => {
            let anyOk = false;
            for (const [key, value] of entries) {
                const c = key === 'torch' ? { advanced: [{ torch: value }] as unknown as MediaTrackConstraintSet[] } : { [key]: value };
                try {
                    await videoTrack.applyConstraints(c);
                    anyOk = true;
                    //console.log(`  ✓ ${key}:`, value);
                } catch (e: any) {
                    //console.log(`  ✗ ${key} not supported:`, e.message);
                }
            }
            //console.log('Done. Settings now:', videoTrack.getSettings());
            //_sConsciousApiService.logEvent("Camera optimizations applied", { userMail: appData?.user.userEmail, applied: Object.keys(desired) });
            onDone?.(anyOk || entries.length === 0 ? 'ok' : 'saved');
        };

        applyNext();
    };

    // Store the live stream in a ref so applySettings can access it reliably at any time
    const streamRef = useRef<MediaStream | null>(null);

    const handleWebcamUserMedia = (stream: MediaStream) => {
        streamRef.current = stream;
        applyConstraints(cameraOptsRef.current, stream);
    };

    const openSettings = () => {
        setPendingOpts({ ...cameraOpts });
        setShowSettings(true);
    };

    const applySettings = (opts: CameraOpts = pendingOpts) => {
        setCameraOpts(opts);
        setPendingOpts(opts);
        cameraOptsRef.current = opts;
        localStorage.setItem('cameraOpts', JSON.stringify(opts));
        // Try streamRef first, fall back to react-webcam's own .stream property
        const stream: MediaStream | null = streamRef.current ?? (webcamRef.current as any)?.stream ?? null;
        if (stream) {
            applyConstraints(opts, stream, (status) => {
                setApplyStatus(status);
                setTimeout(() => setApplyStatus('idle'), 2500);
            });
        } else {
            // No live stream yet — settings saved, will apply when camera opens
            setApplyStatus('saved');
            setTimeout(() => setApplyStatus('idle'), 2500);
        }
        setShowSettings(false);
    };

    const allOff: CameraOpts = { autofocus: false, focusDistance: false, autoExposure: false, autoWhiteBalance: false, disableZoom: false, maxBrightness: false, maxISO: false, maxSharpness: false, torch: false };

    const prevScanningRef = useRef<{fileName: string; image: string, status: string}[]>(appData.scanning);
    const enqueue = async (item: {fileName: string; image: string, status: string}) => {
        setAppData({ ...appData, scanning: [...prevScanningRef.current, item] });
        //appInsights.trackEvent({ name: 'Queueing Receipt', properties: { receipt: item.fileName } });
    };

    const capture = async function() {
        const width = webcamRef.current.video.videoWidth;
        const height = webcamRef.current.video.videoHeight;
        const imageSrc = webcamRef.current.getScreenshot({ width, height });

        // Callback mode: hand image back to parent, don't upload as a receipt.
        if (onCapture) {
            onCapture(imageSrc);
            return;
        }

        setLoading(true);
        const fileName = `${Date.now()}-receipt.jpg`;
        if (!navigator.onLine) {
            navigate('/offline');
            setLoading(false);
            return;
        }
        await fetch('/api/upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                userId: userId,
                imgSrc: imageSrc,
                fileName: fileName
            })
        });
        setAppData({ ...appData, newMessages: (appData.newMessages || 0) + 1, scanning: [...prevScanningRef.current, {fileName: fileName, image: imageSrc, status: "false"}] });
        setLoading(false);
        navigate("/")
    }


    const handleFile = async (file: File) => {
        setSelectedFileName(file.name);
        setLoading(true);
        if (!navigator.onLine) {
            navigate('/offline');
            setLoading(false);
            return;
        }
        const reader = new FileReader();
        reader.onloadend = async () => {
            const fileName = `${Date.now()}-receipt.jpg`;
            await fetch('/api/upload', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userId: userId,
                    imgSrc: reader.result as string,
                    fileName: fileName,
                    appData: appData
                })
            });
            setAppData({ ...appData, newMessages: (appData.newMessages || 0) + 1, scanning: [...prevScanningRef.current, {fileName: fileName, image: reader.result as string, status: "false"}] });
            setLoading(false);
            navigate('/');
        };
        reader.readAsDataURL(file);
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) await handleFile(file);
    };

    const [isDragOver, setIsDragOver] = React.useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
            await handleFile(file);
        }
    };

    // Paste support (Ctrl+V / clipboard screenshot)
    const isUploadScreen = !(page === 'Camera' || onCapture);
    React.useEffect(() => {
        if (!isUploadScreen) return;
        const handlePaste = async (e: ClipboardEvent) => {
            const item = Array.from(e.clipboardData?.items ?? []).find(i => i.type.startsWith('image/'));
            if (!item) return;
            const file = item.getAsFile();
            if (file) await handleFile(file);
        };
        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUploadScreen]);

    const handleDevices = useCallback((mediaDevices: MediaDeviceInfo[]) => {
        setLoading(true)
        const videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput");
        const rearCamera = videoDevices.find(device =>
            /rear|back|environment/i.test(device.label)
        );

        //Log
        //_sConsciousApiService.logEvent("Camera Device Found", {userMail: appData?.user.userEmail, version: appData?.data?.version, devices: mediaDevices, videoDevices: videoDevices, device: rearCamera ? rearCamera.label : videoDevices[0]?.label });

        if (rearCamera) {
            setDevices([rearCamera]);
        } else {
            setDevices([videoDevices[0]]);
        }
        setLoading(false)
    } , []);

    // Feature-detect File API for the upload fallback button
    const fileAccessAvailable = typeof File !== 'undefined' && typeof FileReader !== 'undefined';

    React.useEffect(() => {
        navigator.mediaDevices.getUserMedia({ audio: false, video: true })
        .then(stream => {
            // Permission granted
            stream.getTracks().forEach(track => track.stop()); // Stop the stream if you don't need it
            //Log
            _sConsciousApiService.logEvent("Video permissions granted", {userMail: appData?.user.userEmail, version: appData?.data?.version});
            navigator.mediaDevices.enumerateDevices().then(handleDevices);
        })
        .catch(error => {
            //Log
            _sConsciousApiService.logEvent("Video permissions denied", {userMail: appData?.user.userEmail, version: appData?.data?.version});
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                if (onCapture) {
                    // Overlay mode: close the overlay so the parent can handle it
                    onClose?.();
                } else {
                    navigate('/permissions-required');
                }
            }
        });
    }, [handleDevices]);

    const buttonStyles = {
        root: {
            borderRadius: "20px", // Makes the button circular
            width: 150,
            height: 40,
            backgroundColor: "white",
            fontSize: '12',
            color: "black",
        },
        icon: {
            fontSize: 20,
        },
    };

    const swipeTouchStartX = useRef<number | null>(null);
    const swipeTouchStartY = useRef<number | null>(null);
    const SWIPE_ROUTES = ['/', '/Camera', '/Chat', '/Reports', '/Menu'];

    const handleSwipeTouchStart = (e: React.TouchEvent) => {
        swipeTouchStartX.current = e.touches[0].clientX;
        swipeTouchStartY.current = e.touches[0].clientY;
    };

    const handleSwipeTouchEnd = (e: React.TouchEvent) => {
        if (swipeTouchStartX.current === null || swipeTouchStartY.current === null) return;
        const dx = e.changedTouches[0].clientX - swipeTouchStartX.current;
        const dy = e.changedTouches[0].clientY - swipeTouchStartY.current;
        swipeTouchStartX.current = null;
        swipeTouchStartY.current = null;
        if (Math.abs(dx) < 60 || Math.abs(dx) <= Math.abs(dy) * 1.5) return;
        const currentIndex = SWIPE_ROUTES.indexOf('/Camera');
        if (dx < 0) {
            if (currentIndex + 1 < SWIPE_ROUTES.length) navigate(SWIPE_ROUTES[currentIndex + 1]);
        } else {
            if (currentIndex - 1 >= 0) navigate(SWIPE_ROUTES[currentIndex - 1]);
        }
    };

    return ( 
        <>
            {
                loading ?  

                    <ProgressBar label={errorMessage !== null ? errorMessage : "Queuing receipt"} error={errorMessage !== null ? true : false} progress={30} />

                :

                    page === "Camera" || onCapture ? 

                        <Stack 
                            tokens={{ childrenGap: 10 }}
                            horizontalAlign="center" 
                            style={{ 
                                position: 'fixed',
                                top: 0, 
                                left: 0,
                                height: '100dvh', 
                                width: '100vw', 
                                background: 'black', 
                                zIndex: 9999,
                            }}
                        >
                            <Webcam 
                                style={{ 
                                    height: '100dvh', 
                                    width: '100vw', 
                                    objectFit: 'cover' 
                                }} 
                                audio={false} 
                                ref={webcamRef} 
                                imageSmoothing={true}
                                screenshotFormat="image/jpeg"
                                screenshotQuality={1}
                                onUserMedia={handleWebcamUserMedia}
                                videoConstraints={{ 
                                    width: { ideal: 1920 }, 
                                    height: { ideal: 1080 },

                                    ...(devices.length > 1 ? {facingMode: { exact: "environment" }} : {deviceId: devices[0]?.deviceId})
                                }}
                            />

                            {/* Close button — callback mode closes overlay; standalone mode goes home */}
                            {(onClose || (!onCapture)) && (
                                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10001 }}>
                                    <button
                                        className="closeBtn"
                                        title={onClose ? 'Close camera' : 'Back'}
                                        aria-label={onClose ? 'Close camera' : 'Back'}
                                        onClick={onClose ?? (() => navigate('/'))}
                                    >✕</button>
                                </div>
                            )}

                            {/* Settings button — overlaid on video, top-right, inside camera frame */}
                            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10001 }}>
                                {applyStatus === 'ok' && (
                                    <div style={{ position: 'absolute', top: 44, right: 0, background: '#22c55e', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: '0.78em', fontWeight: 700, whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>Applied ✓</div>
                                )}
                                {applyStatus === 'saved' && (
                                    <div style={{ position: 'absolute', top: 44, right: 0, background: '#f59e0b', color: '#111', borderRadius: 8, padding: '4px 10px', fontSize: '0.78em', fontWeight: 700, whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>Saved</div>
                                )}
                                {applyStatus === 'error' && (
                                    <div style={{ position: 'absolute', top: 44, right: 0, background: '#ef4444', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: '0.78em', fontWeight: 700, whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>Failed</div>
                                )}
                                <IconButton
                                    iconProps={{ iconName: 'Settings' }}
                                    title="Camera settings"
                                    ariaLabel="Camera settings"
                                    onClick={showSettings ? () => setShowSettings(false) : openSettings}
                                    styles={{
                                        root: { backgroundColor: '#ffcc00', color: '#000', borderRadius: '50%', width: 38, height: 38 },
                                        rootHovered: { backgroundColor: '#e6b800', color: '#000' },
                                        rootPressed: { backgroundColor: '#ccaa00', color: '#000' },
                                        icon: { color: '#000' },
                                    }}
                                />
                            </div>

                            {/* Settings panel */}
                            {showSettings && (
                                <div style={{
                                    position: 'absolute', top: 54, right: 8, zIndex: 10001,
                                    background: 'rgba(20,20,20,0.95)', borderRadius: 14,
                                    padding: '14px 16px', minWidth: 260,
                                    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                                    color: '#fff', textAlign: 'left',
                                }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.85em', marginBottom: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Camera Optimizations</div>
                                    {OPT_LABELS.map(({ key, label }) => {
                                        const isSupported = Object.keys(supportedCaps).length === 0 || supportedCaps[key] !== false;
                                        return (
                                            <label key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '5px 0', cursor: isSupported ? 'pointer' : 'default', fontSize: '0.85em', opacity: isSupported ? 1 : 0.38 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {label}
                                                    {Object.keys(supportedCaps).length > 0 && (
                                                        <span style={{ fontSize: '0.7em', borderRadius: 10, padding: '1px 6px', background: isSupported ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.08)', color: isSupported ? '#4ade80' : 'rgba(255,255,255,0.3)', fontWeight: 600, letterSpacing: 0.3 }}>
                                                            {isSupported ? 'ok' : 'n/a'}
                                                        </span>
                                                    )}
                                                </span>
                                                <input
                                                    type="checkbox"
                                                    checked={pendingOpts[key]}
                                                    disabled={!isSupported}
                                                    onChange={e => isSupported && setPendingOpts(prev => ({ ...prev, [key]: e.target.checked }))}
                                                    style={{ width: 16, height: 16, accentColor: '#ffcc00', flexShrink: 0, cursor: isSupported ? 'pointer' : 'default' }}
                                                />
                                            </label>
                                        );
                                    })}
                                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginTop: 10, paddingTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <button
                                            onClick={() => applySettings(allOff)}
                                            style={{ flex: 1, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 0', fontSize: '0.8em', cursor: 'pointer' }}
                                        >Disable all</button>
                                        <button
                                            onClick={() => applySettings(defaultOpts)}
                                            style={{ flex: 1, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', padding: '6px 0', fontSize: '0.8em', cursor: 'pointer' }}
                                        >Enable all</button>
                                        <button
                                            onClick={() => applySettings()}
                                            style={{ width: '100%', background: '#ffcc00', border: 'none', borderRadius: 8, color: '#111', padding: '8px 0', fontSize: '0.88em', fontWeight: 800, cursor: 'pointer', marginTop: 2 }}
                                        >Apply</button>
                                    </div>
                                </div>
                            )}

                            <FontIcon
                                onClick={capture}
                                aria-label="Camera"
                                iconName={"Camera"}
                                className="FontIcon"
                                style={{
                                    position: 'absolute',
                                    bottom: 36, // 10% smaller than 40
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: 32.4, // 10% smaller than 36
                                    color: 'white',
                                    background: 'rgba(255, 204, 0, 0.3)', // #ffcc00 with opacity
                                    borderRadius: '24.3px', // 10% smaller than 27px
                                    padding: '9.72px 38.88px', // 10% smaller than 10.8px 43.2px
                                    boxShadow: '0 1.8px 7.2px rgba(0,0,0,0.2)', // 10% smaller than 2px 8px
                                    cursor: 'pointer',
                                    border: 'none',
                                }}
                            />
                        </Stack>
 
                    :

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: '0 5%' }}>
                        {fileAccessAvailable ? (
                            <>
                                {/* Drop zone */}
                                <div
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{
                                        width: '100%',
                                        maxWidth: 360,
                                        aspectRatio: '4/3',
                                        border: `2px dotted ${isDragOver ? '#ffcc00' : 'rgba(255,204,0,0.55)'}`,
                                        borderRadius: 16,
                                        background: isDragOver ? 'rgba(255,204,0,0.06)' : '#000',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 10,
                                        cursor: 'pointer',
                                        transition: 'border-color 0.15s, background 0.15s',
                                        userSelect: 'none',
                                    }}
                                >
                                    <MdUpload style={{ fontSize: '2.4rem', color: isDragOver ? '#ffcc00' : 'rgba(255,204,0,0.6)', transition: 'color 0.15s' }} />
                                    <span style={{ color: isDragOver ? '#ffcc00' : 'rgba(255,204,0,0.6)', fontSize: '0.84rem', fontWeight: 600, transition: 'color 0.15s', textAlign: 'center' }}>
                                        {isDragOver ? 'Drop to upload' : 'Tap to browse · drag & drop · paste'}
                                    </span>
                                    {selectedFileName && (
                                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.74rem', fontFamily: 'monospace', maxWidth: '80%', textAlign: 'center', wordBreak: 'break-all' }}>
                                            {selectedFileName}
                                        </span>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                            </>
                        ) : (
                            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', textAlign: 'center' }}>
                                File access is not available on this device.
                            </p>
                        )}
                    </div>
            }
        </>
    )
}